/*
  AE: Convermax fitment table on the product page — newest year range first, and only as many
  rows as fit beside the gallery before "Show More".
  Convermax renders the table client-side (no sort or row-count setting) as two bodies: rows
  always shown, and a collapsible body behind its Show More button. Nodes are never moved (the
  app re-renders them); the bodies become `display: contents` in one flex column, rows get a
  flex `order`, and rows past the fold get `data-ae-extra`, hidden until the table is opened.
  Open/closed is read from Convermax's own button label ("Show Less" = open), because its
  collapse classes don't track the state. Styles: ae-imm.css section 15.

  Sort: end year, then start year, both newest first; then make, model, submodel A-Z.
  Fold: on desktop the right column ends level with the gallery card (never fewer than
  MIN_ROWS); on phones MOBILE_ROWS.

  Cost: loaded on product templates only (snippets/stylesheets.liquid). It watches just the
  fitment panel, re-sorts only when Convermax's rows change, and re-measures (one layout read)
  on resize or when the gallery changes height, at most once per frame.
*/
(() => {
  const MIN_ROWS = 5;
  const MOBILE_ROWS = 8;
  const DESKTOP = window.matchMedia('(min-width: 990px)');
  const panel = document.querySelector('main [class*="__fitment_panel_ae"]');
  if (!panel) return;

  const field = (row, name) => (row.querySelector(`.fitment-field-${name}`)?.textContent || '').trim();
  const sortKey = (row) => {
    const nums = field(row, 'year').match(/\d{4}/g) || ['0'];
    return {
      row,
      end: Number(nums[nums.length - 1]),
      start: Number(nums[0]),
      rest: [field(row, 'make'), field(row, 'model'), field(row, 'submodel')],
    };
  };
  const compare = (a, b) =>
    b.end - a.end ||
    b.start - a.start ||
    a.rest[0].localeCompare(b.rest[0]) ||
    a.rest[1].localeCompare(b.rest[1]) ||
    a.rest[2].localeCompare(b.rest[2]);

  let sorted = [];
  let signature = '';

  const sort = (table) => {
    const rows = [...table.querySelectorAll('.fitment-table-fitment .fitment-row')];
    const sig = rows.length + '|' + rows.map((r) => r.textContent).join('|').length;
    if (sig === signature) return false;
    signature = sig;
    sorted = rows.map(sortKey).sort(compare).map((k) => k.row);
    sorted.forEach((row, i) => {
      row.style.order = String(i + 1);
      row.toggleAttribute('data-ae-odd', i % 2 === 1);
    });
    table.classList.add('ae-fit-sorted');
    return true;
  };

  const fold = (table) => {
    if (!sorted.length) return;
    let keep = MOBILE_ROWS;
    const media = document.querySelector('main .product-information__media');
    if (DESKTOP.matches && media) {
      const rowHeight = sorted[0].getBoundingClientRect().height || 26;
      const shown = sorted.filter((r) => r.getClientRects().length).length; // rows on screen now
      const box = panel.getBoundingClientRect();
      const chrome = box.height - shown * rowHeight; // heading, header row, button, padding
      keep = Math.max(MIN_ROWS, Math.floor((media.getBoundingClientRect().bottom - box.top - chrome) / rowHeight));
    }
    sorted.forEach((row, i) => row.toggleAttribute('data-ae-extra', i >= keep));
    table.classList.toggle('ae-fit-all-shown', keep >= sorted.length);
  };

  let queued = false;
  let resort = true;
  const run = () => {
    queued = false;
    const table = panel.querySelector('.cm_fitment-table');
    if (!table) return;
    if (resort) sort(table);
    resort = false;
    fold(table);
    const button = table.querySelector('.cm_fitment-table_collapse-button');
    table.classList.toggle('ae-fit-open', /less/i.test(button?.textContent || ''));
  };
  const queue = (needsSort) => {
    if (needsSort) resort = true;
    if (queued) return;
    queued = true;
    requestAnimationFrame(run);
  };

  // Rows arrive after load and change with the garage vehicle, and the button label flips on
  // click; only this panel is watched
  new MutationObserver(() => queue(true)).observe(panel, { childList: true, subtree: true, characterData: true });
  window.addEventListener('resize', () => queue(false), { passive: true });
  DESKTOP.addEventListener('change', () => queue(false));
  const media = document.querySelector('main .product-information__media');
  if (media && 'ResizeObserver' in window) new ResizeObserver(() => queue(false)).observe(media);
  queue(true);
})();
